import * as d3 from 'd3';
import _ from 'lodash';

import Constants from '../Constants';
import ImageRegistry from '../ImageRegistry';
import { getTopicUrl, getTopicWord } from '../topicUtils';
import TopicVizScaling from '../TopicVizScaling';
import { euclideanDistance, median } from '../Utils';
import Circle from '../VirtualDom/drawElements/Circle';
import LabeledImage from '../VirtualDom/drawElements/LabeledImage';
import VirtualDom from '../VirtualDom/VirtualDom';
import VirtualDomElement from '../VirtualDom/VirtualDomElement';
import VirtualDomLayer from '../VirtualDom/VirtualDomLayer';

export default function createTopicLayer(
  clusterInfo,
  virtualDom: VirtualDom,
  scaling: TopicVizScaling,
  imageRegistry: ImageRegistry,
) {
  const documentPointsByClusterId = {};

  for (const documentPoint of virtualDom
    .getLayer('documentPoints')
    .getElements()) {
    const clusterId = documentPoint.getAttribute('clusterId');
    if (!(clusterId in documentPointsByClusterId)) {
      documentPointsByClusterId[clusterId] = [documentPoint];
    } else {
      documentPointsByClusterId[clusterId].push(documentPoint);
    }
  }

  const topicElements = [];

  const numbersOfDocumentsInClusters = clusterInfo
    .filter(cluster => cluster['cluster_index'] in documentPointsByClusterId)
    .map(cluster => documentPointsByClusterId[cluster['cluster_index']].length);

  const [minDocumentsInCluster, maxDocumentsInCluster] = d3.extent(
    numbersOfDocumentsInClusters,
  );

  const scaleFontSizeByClusterSize = d3
    .scaleLinear()
    .domain([+minDocumentsInCluster, +maxDocumentsInCluster])
    .range([Constants.topics.minFontSize, Constants.topics.maxFontSize]);

  //const scaleNumberOfSubTopicsByClusterSize=(el)=>9;

  const maxTotalRadius = 1000;
  const maxRadius = (4 * maxTotalRadius) / clusterInfo.length;
  d3.scaleLinear()
    .domain([+minDocumentsInCluster, +maxDocumentsInCluster])
    .range([maxRadius / 4, maxRadius]);

  //draw smaller clusters on top of bigger clusters
  //filter clusters that have datapoints
  const sortedClustersBySize = _.sortBy(
    clusterInfo.filter(
      cluster => cluster['cluster_index'] in documentPointsByClusterId,
    ),
    cluster => -documentPointsByClusterId[cluster['cluster_index']].length,
  );

  for (const cluster of sortedClustersBySize) {
    if (cluster['cluster_index'] in documentPointsByClusterId) {
      const clusterIndex = cluster['cluster_index'];

      const documents = documentPointsByClusterId[clusterIndex];
      const medianX = median(documents.map(doc => doc.getAttribute('x')));
      const medianY = median(documents.map(doc => doc.getAttribute('y')));

      const dummyCenter = new VirtualDomElement();
      dummyCenter.set('x', medianX);
      dummyCenter.set('y', medianY);
      documents.map(documentPoint =>
        euclideanDistance(dummyCenter, documentPoint),
      );

      // TOPICS (top 1 topic words)
      const topicUrl = getTopicUrl(cluster, 0); //get first picture
      const topicElement = new LabeledImage({
        x: medianX,
        y: medianY,
        maxCharacterWidth: 7,
        maxLines: 2,
        ellipsis: false,
        label: getTopicWord(cluster, 0).toUpperCase(),
        borderColor: scaling.colorScale(clusterIndex),
        url: topicUrl,
        fontSize: scaleFontSizeByClusterSize(documents.length),
        imageRegistry: imageRegistry,
      });

      topicElement.setAttribute('clusterIndex', clusterIndex);

      topicElement.set('clusterIndex', clusterIndex);
      topicElement.set('countDocuments', numbersOfDocumentsInClusters);

      const topicCircleElement = new Circle({
        x: medianX,
        y: medianY,
        radius:
          Constants.topics.scaleFactorCircle *
          Math.sqrt(documents.length) *
          scaling.getScaleFactor(),
        //radius:scaleCircleRadiusByClusterSize(documents.length),
        fillColor: scaling.colorScale(clusterIndex),
        strokeColor: '#444',
        strokeWidth: 4,
        opacity: 0.2,
      });
      topicCircleElement.set('clusterIndex', clusterIndex);
      topicCircleElement.set(
        'countDocuments',
        numbersOfDocumentsInClusters[clusterIndex],
      );

      topicElements.push(topicCircleElement);
      topicElements.push(topicElement);

      scaling.getScaleFactor();
    }
  }

  const topicSelection = new VirtualDomLayer({
    name: 'topicElements',
    elements: topicElements,
    keyFramesOpacity: [
      [Constants.thresholds.topic[0], 1],
      [Constants.thresholds.topic[1], 1],
      [Constants.thresholds.topic[1] + 0.2, 0],
    ],
    showAtInitialZoom: true,
  });
  virtualDom.registerLayer(topicSelection);
}
